import { NavigateService } from '@shared/services/navigate.service';
import { StorageService } from '@core/services/storage.service';
import { TaskType } from '@shared/enums/task-type.enum';
import { catchError, filter, tap } from 'rxjs/operators';
import { EMPTY, Subscription } from 'rxjs';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TaskSidenavManager } from '@shared/modules/task-sidenav/managers/task-sidenav.manager';
import { PermissionsGroups } from '@core/permissions/permissions.group';
import { Task } from '@shared/models/task.model';
import { Config } from '@shared/configs/config';
import {
  ListTaskEvent,
  ListTaskEventType,
  ListTaskService
} from '@shared/modules/list/services/list-task.service';
import { TaskController } from '@shared/controllers/task.controller';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { TaskPermissionController } from '@shared/controllers/task-permission.controller';
import { Project } from '@modules/projects/shared/models/project.model';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ChangeType } from '@shared/modules/task-sidenav/enums/change-type.enum';

@Component({
  selector: 'task-basic-preview-data',
  templateUrl: './task-basic-preview-data.component.html',
  styleUrls: ['./task-basic-preview-data.component.scss']
})
export class TaskBasicPreviewDataComponent implements OnInit, OnDestroy {
  PermissionsGroups = PermissionsGroups;
  Config = Config;
  TaskType = TaskType;

  sub: Subscription = new Subscription();
  taskManager: TaskSidenavManager = new TaskSidenavManager();
  taskPermission: TaskPermissionController = new TaskPermissionController();

  get shouldShowProjectInfo(): boolean {
    return (
      !!this.service?.task?.assignedTo?.[0]?.userPerson?.previewName ||
      !!this.service?.task?.assignedEmails?.length ||
      !!this.service?.task?.project?.fullAddress
    );
  }

  get assignedDisplayName(): string {
    return (
      this.service?.task?.assignedTo?.[0]?.userPerson?.previewName ||
      this.service?.task?.assignedEmails?.[0] ||
      ''
    );
  }

  get isProcessTaskContext(): boolean {
    return (
      !!this.service?.isProcessTaskContext ||
      this.service?.groupType === 'activity-template' ||
      !!this.service?.activityTemplateContext
    );
  }

  protected readonly changeTypes = Object.values(ChangeType).map((key) => ({
    id: key as ChangeType,
    name: this.t.instant(`Projects.changeType.${key}`)
  })) as Array<{ id: ChangeType; name: string }>;

  constructor(
    public storage: StorageService,
    public service: TaskSidenavService,
    private changes: ChangeDetectorRef,
    private listTaskService: ListTaskService,
    private navigateService: NavigateService,
    private snackBarService: SnackBarService,
    public t: TranslateService
  ) {}

  ngOnInit() {
    this.listenListTaskEvents();
    this.subscribeTaskUpdate();
    this.initPermissions();
  }

  initPermissions() {
    this.taskPermission.initPermissions(this.service.task);
    this.changes.detectChanges();
  }

  listenListTaskEvents() {
    const actions = [ListTaskEventType.UPDATE_TASK];
    const sub = this.listTaskService.emitter
      .pipe(filter((i) => actions.indexOf(i.type) > -1))
      .subscribe(this.updateTask.bind(this));
    this.sub.add(sub);
  }

  updateTaskFromCheckbox({ task, subtasksCompleted }: { task: Task; subtasksCompleted?: boolean }) {
    const ctrl = new TaskController(task);
    ctrl
      .changeTaskStatus(task, this.listTaskService.isMyWorkTaskLists && subtasksCompleted === false)
      .pipe(
        tap(() => {
          this.snackBarService.success(this.getStatusChangedMessage(task));
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.UPDATE_COUNTERS,
            data: { projectId: task?.project?.id, groupType: task.type }
          });

          this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_TASK_LIST });
          this.changes.detectChanges();
        }),
        catchError(() => {
          this.snackBarService.error(this.t.instant('Tasks.errorStatusChange'));
          return EMPTY;
        })
      )
      .subscribe();
  }

  private getStatusChangedMessage(task: Task): string {
    const taskType = task?.type === 'company-task' || task?.type === 'client-task' ? 'task' : task?.type || 'task';
    const actionKey = task?.status === 'completed' ? 'completed' : 'created';

    return this.t.instant('Tasks.successStatusChangeAction', {
      taskType: this.t.instant(`Tasks.TaskType.${taskType}`),
      action: this.t.instant(`Tasks.statusAction.${actionKey}`)
    });
  }

  updateTask(e: ListTaskEvent) {
    if (e.data.id === this.service.task.id) {
      this.service.initPreviewState(e.data);
      this.service.form.get(TASK_SIDENAV_FORM.status).patchValue(e.data.status, { emitEvent: false });
      this.changes.detectChanges();
    }
  }

  onStatusChange(task: Task) {
    this.service.setStatusCompleted(task);
  }

  checkedSubtasks(checked: boolean) {}

  subscribeTaskUpdate() {
    const sub = this.service.taskActionEmitter.subscribe(() => this.changes.detectChanges());
    this.sub.add(sub);
  }

  openTaskEdit() {
    this.taskManager.editTask(this.service?.task, this.listTaskService.isMyWorkTaskLists);
  }

  openParentPreview(task: Task) {
    this.taskManager.previewTask(task);
  }

  navigateToProject(project?: Project) {
    if (!project) return;

    this.navigateService
      .goWithPromise('project-preview-summary', { projectId: project?.id })
      .then(() => this.service.closeTaskSidenav());
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
